/**
 * client/src/lib/payment-rail/stepup-prf-carry.ts
 *
 * Carries the officer secret keys derived from an AAL3 STEP-UP assertion to the
 * gated read that step-up was performed for, so one tap serves both.
 *
 * WHY THIS EXISTS. Opening a counterparty's receiving account cost the reader
 * TWO taps of the same security key, ~26 seconds apart (confirmed in production
 * 2026-08-18: `auth.aal.refused` → `auth.stepup.begin mode=hardware` →
 * `auth.stepup.complete newAal=AAL3`, then a second ceremony inside
 * `withOfficerSecretKeys`). Both proved the identical fact — possession of that
 * key, right now. The second proved nothing the first had not.
 *
 * The step-up's own assertion ALREADY carries PRF output: `beginStepUp`'s
 * hardware and single arms request `prf.eval.first` seeded with the profile's
 * `prfSalt` (server/logic/webauthn/step-up/begin.ts), which is the SAME salt
 * `withOfficerSecretKeys` reads off `/authenticate/begin`. Same credential +
 * same salt ⇒ byte-identical PRF output ⇒ the same officer keys. The client
 * simply discarded it and prompted again to regenerate what it had just thrown
 * away.
 *
 * WHAT THIS IS NOT. It relaxes NO server-side control. The AAL3 route guard is
 * untouched and still refuses at AAL2; the step-up assertion is still verified
 * and still promotes the session server-side; the envelope read is still
 * AAL-gated, still projects one slot, still emits its own audit line. The only
 * thing reused is a local key derivation the reader already paid for. There is
 * no new route, no new crypto, and no server change of any kind.
 *
 * CREDENTIAL PIN IS CORRECTNESS, NOT CAUTION. PRF output is per CREDENTIAL
 * (webauthn-credential-pin.ts, ledger #47). An officer who re-enrolled holds
 * accounts sealed to an older credential than the one their step-up asserted
 * with; keys carried from the wrong credential would fail the envelope's auth
 * tag and surface as data corruption on a healthy record. `take()` therefore
 * returns nothing unless the sealing credential is exactly the one that
 * asserted — that reader keeps today's second ceremony, which is the correct
 * outcome, not a degraded one.
 *
 * L0-9 — THE WIDENING, NAMED. `withOfficerSecretKeys` holds its derived keys
 * only across a local unwrap. This holds them across a network round-trip (the
 * envelope fetch), so "wiped immediately after derivation" becomes "wiped after
 * one fetch". That is a real widening of the window and is bounded three ways,
 * every one of them a wipe:
 *   1. SINGLE USE — `take()` removes the entry before returning it; a second
 *      caller gets nothing, so the keys cannot be replayed by a later read.
 *   2. HARD TTL — a timer wipes at CARRY_TTL_MS regardless of what the UI does.
 *      A reader who walks away mid-flow does not leave key material live.
 *   3. SUPERSEDE + UNLOAD — any new stash wipes the previous entry, and
 *      `pagehide` wipes on navigation away.
 * The raw PRF output is never held at all: `deriveOfficerKeyPair` consumes and
 * wipes it inside `stash()`, so only the derived secrets exist to be carried.
 * No plaintext, no DEK, and no account digits pass through this module.
 *
 * DELIBERATELY OPT-IN AT THE READ SITE. Nothing consumes this carry implicitly.
 * `openAccountEnvelope` ignores it unless a caller passes `allowStepUpCarry`,
 * because the safety argument is "the reader stepped up in order to complete
 * THIS action and is completing it now" — a claim only the surface driving both
 * halves can make. A module-global that any officer read silently consumed
 * would extend one tap to actions the reader never connected to it.
 */

import type { AuthenticationResponseJSON } from "@simplewebauthn/browser";
import type { AuthenticationExtensionsClientOutputs } from "@simplewebauthn/browser";

import { atomicWipe } from "@/lib/prf-key-derivation";
import { extractPrfOutput, extractPrfSaltFromOptions } from "@/lib/webauthn-prf-extension";
import { deriveOfficerKeyPair } from "./officer-key-derivation";
import type { OfficerSecrets } from "./officer-envelope-open";

/**
 * How long carried keys may live. Generous enough for the fetch the reveal
 * surface runs immediately after a successful step-up (sub-second in practice,
 * plus a cold Render instance's worst case), short enough that key material
 * never outlives the interaction that produced it. NOT a possession window:
 * `take()` is single-use, so this is only a backstop for the flow that never
 * completes.
 */
const CARRY_TTL_MS = 60_000;

interface CarriedEntry {
  /** The credential that actually asserted during the step-up. */
  credentialId: string;
  secrets: OfficerSecrets;
  timer: ReturnType<typeof setTimeout>;
}

let carried: CarriedEntry | null = null;

/** Zero both keys and drop the entry. Idempotent — every exit path calls it. */
function wipeCarried(): void {
  if (!carried) return;
  const entry = carried;
  carried = null; // cleared FIRST so a re-entrant call cannot double-wipe
  clearTimeout(entry.timer);
  atomicWipe(entry.secrets.mlkemSk);
  atomicWipe(entry.secrets.ecSk);
}

/** Public wipe — for a caller abandoning a flow (unmount, cancel, sign-out). */
export function clearStepUpCarry(): void {
  wipeCarried();
}

if (typeof window !== "undefined") {
  // Navigating away must not leave derived key material in a bfcached page.
  window.addEventListener("pagehide", wipeCarried);
}

/**
 * Derive and hold the officer keys proven by a completed step-up assertion.
 *
 * Called ONLY after the server has verified the assertion and promoted the
 * session — never on the strength of a client-side assertion alone, so this
 * cannot become a way to hold keys for a ceremony the server rejected.
 *
 * Silently no-ops when the authenticator returned no PRF output or the options
 * carried no salt (an AAL2 arm, or a profile with no `prfSalt`): the caller
 * then simply gets today's second ceremony. A missing carry is always safe.
 */
export function stashStepUpOfficerSecrets(args: {
  assertionResponse: AuthenticationResponseJSON;
  options: unknown;
}): void {
  // Supersede any previous entry before deriving — never two live sets.
  wipeCarried();

  const prfOutput = extractPrfOutput(
    args.assertionResponse.clientExtensionResults as
      | AuthenticationExtensionsClientOutputs
      | undefined,
  );
  if (!prfOutput) return;

  const prfSalt = extractPrfSaltFromOptions(args.options as Record<string, unknown>);
  if (!prfSalt) {
    // L0-9: PRF output was extracted but is unusable without its salt. Nothing
    // will consume it, so wipe here rather than let 32 bytes fall out of scope.
    atomicWipe(prfOutput);
    return;
  }

  // deriveOfficerKeyPair wipes prfOutput internally (#970) — do NOT wipe here.
  const secrets = deriveOfficerKeyPair(prfOutput, prfSalt);

  carried = {
    credentialId: args.assertionResponse.id,
    secrets,
    timer: setTimeout(wipeCarried, CARRY_TTL_MS),
  };
}

/**
 * Run `fn` with keys carried from a step-up, IF they were derived from the
 * credential `webauthnCredId` names. Returns `{ used: false }` when there is no
 * carry or the credential differs — the caller must then run its own ceremony.
 *
 * The entry is consumed (and wiped) whether or not `fn` throws: a failed unwrap
 * must not leave the keys available to a retry, which would be a second use of
 * a single-use carry.
 */
export async function withCarriedOfficerSecrets<T>(
  webauthnCredId: string,
  fn: (secrets: OfficerSecrets) => Promise<T>,
): Promise<{ used: true; value: T } | { used: false }> {
  const entry = carried;
  if (!entry || entry.credentialId !== webauthnCredId) return { used: false };

  // Detach before running so a re-entrant call cannot see the same keys, then
  // wipe in the finally — the keys are dead the instant `fn` settles.
  carried = null;
  clearTimeout(entry.timer);
  try {
    return { used: true, value: await fn(entry.secrets) };
  } finally {
    atomicWipe(entry.secrets.mlkemSk);
    atomicWipe(entry.secrets.ecSk);
  }
}
